import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './App.css';
import GlobalInfo from './components/GlobalInfo';
import InvestmentsTable from './components/InvestmentsTable';
import LogsContainer from './components/LogsContainer';
import {
  MagnifyingGlass,
  Plus,
  Bank,
  Sun,
  Moon,
  X,
  Sparkle,
  GearSix,
  TrashSimple,
  ArrowsClockwise,
  WarningCircle,
  ShieldCheck,
  CheckCircle,
  SlidersHorizontal,
} from '@phosphor-icons/react';

function App() {
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);
  const [stockReport, setStockReport] = useState(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [searchTicker, setSearchTicker] = useState('');
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [showAddInvestmentModal, setShowAddInvestmentModal] = useState(false);
  const [addInvestmentForm, setAddInvestmentForm] = useState({
    ticker: '',
    budget: '',
    exit_condition: '',
    step_trigger_percentages: [0.0, -3.0, -5.0, -10.0, -20.0, -40.0, -70.0],
    auto_restart: false,
  });
  const [isAddingInvestment, setIsAddingInvestment] = useState(false);
  const [showConfirmAddInvestment, setShowConfirmAddInvestment] =
    useState(false);
  const [confirmAddInvestmentData, setConfirmAddInvestmentData] =
    useState(null);
  const [otpToken, setOtpToken] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otpExpiresIn, setOtpExpiresIn] = useState(0);
  const [sortBy, setSortBy] = useState('default');
  const [showDetailBalanceModal, setShowDetailBalanceModal] = useState(false);
  const [detailBalanceData, setDetailBalanceData] = useState(null);
  const [isLoadingDetailBalance, setIsLoadingDetailBalance] = useState(false);
  const [detailBalanceOtpToken, setDetailBalanceOtpToken] = useState(null);
  const [detailBalanceOtpCode, setDetailBalanceOtpCode] = useState('');
  const [showDetailBalanceOtpInput, setShowDetailBalanceOtpInput] =
    useState(false);
  const [detailBalanceOtpExpiresIn, setDetailBalanceOtpExpiresIn] = useState(0);

  // Search Results State
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResultsModal, setShowSearchResultsModal] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [networkStatus, setNetworkStatus] = useState('online');

  // Delete Investment State
  const [deleteOtpToken, setDeleteOtpToken] = useState(null);
  const [deleteOtpCode, setDeleteOtpCode] = useState('');
  const [showDeleteOtpInput, setShowDeleteOtpInput] = useState(false);
  const [deleteOtpExpiresIn, setDeleteOtpExpiresIn] = useState(0);
  const [isDeletingInvestment, setIsDeletingInvestment] = useState(false);
  const [tickerToDelete, setTickerToDelete] = useState('');

  // Update Settings State
  const [showUpdateSettingsModal, setShowUpdateSettingsModal] = useState(false);
  const [updateSettingsForm, setUpdateSettingsForm] = useState({
    ticker: '',
    exit_condition: '',
    auto_restart: false,
  });
  const [updateSettingsOtpToken, setUpdateSettingsOtpToken] = useState(null);
  const [updateSettingsOtpCode, setUpdateSettingsOtpCode] = useState('');
  const [showUpdateSettingsOtpInput, setShowUpdateSettingsOtpInput] =
    useState(false);
  const [updateSettingsOtpExpiresIn, setUpdateSettingsOtpExpiresIn] =
    useState(0);
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);

  const intervalRef = useRef(null);
  const searchInputRef = useRef(null);

  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return (
      savedTheme ||
      (window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light')
    );
  });

  useEffect(() => {
    document.body.className = theme + '-theme';
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  const fetchData = useCallback(
    async (sortParam = sortBy) => {
      try {
        const dataResponse = await fetch(`/api/data?sort_by=${sortParam}`);
        if (!dataResponse.ok) {
          throw new Error(`HTTP error! status: ${dataResponse.status}`);
        }
        const dataResult = await dataResponse.json();
        setData(dataResult);

        const logsResponse = await fetch('/api/logs');
        if (!logsResponse.ok) {
          throw new Error(`HTTP error! status: ${logsResponse.status}`);
        }
        if (logsResponse.status !== 208 || logs.length === 0) {
          const logsResult = await logsResponse.json();
          setLogs(logsResult);
        }

        setError(null);
      } catch (e) {
        let errorMessage = 'Failed to load data.';
        if (e.name === 'TypeError' && e.message.includes('Failed to fetch')) {
          errorMessage = '백엔드 서버가 실행 중인지 확인해주세요.';
        } else if (e.message.includes('HTTP error')) {
          errorMessage = `서버 오류가 발생했습니다: ${e.message}`;
        } else {
          errorMessage = `데이터 로드 중 오류가 발생했습니다: ${e.message}`;
        }
        setError(errorMessage);
        setData(null);
        setLogs([]);
      }
    },
    [logs.length, sortBy]
  );

  const handleSortChange = newSortBy => {
    setSortBy(newSortBy);
    fetchData(newSortBy);
  };

  const formatMarkdownTable = text => {
    const lines = text.split('\n');
    const formattedLines = lines.map(line => {
      if (line.includes('|')) {
        const cells = line
          .split('|')
          .map(cell => cell.trim())
          .filter(cell => cell.length > 0);
        if (cells.length > 0) {
          return `| ${cells.join(' | ')} |`;
        }
      }
      return line;
    });
    return formattedLines.join('\n');
  };

  const fetchStockReport = async ticker => {
    setIsLoadingReport(true);
    setIsAnalyzing(false);
    setShowReportModal(true);
    setStockReport(null);

    try {
      const response = await fetch(`/api/stock-report/stream?ticker=${ticker}`);
      if (!response.ok || !response.body) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      let isStreaming = true;
      while (isStreaming) {
        const { value, done } = await reader.read();
        if (done) {
          isStreaming = false;
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const evt of events) {
          if (!evt.trim()) continue;
          const lines = evt.split('\n');
          let eventType = 'message';
          let dataStr = '';

          for (const line of lines) {
            if (line.startsWith('event:')) {
              eventType = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              const val = line.slice(5).trim();
              dataStr += (dataStr ? '\n' : '') + val;
            }
          }

          if (eventType === 'checklist') {
            setIsLoadingReport(false);
            setIsAnalyzing(true);
            const formatted = formatMarkdownTable(dataStr);
            setStockReport(prev => ({
              ...(prev || {}),
              check_list: formatted,
            }));
          } else if (eventType === 'status' && dataStr === 'analyzing') {
            setIsAnalyzing(true);
          } else if (eventType === 'analysis') {
            setIsAnalyzing(false);
            const formatted = formatMarkdownTable(dataStr);
            setStockReport(prev => ({
              ...(prev || {}),
              analysis: formatted,
            }));
          } else if (eventType === 'error') {
            throw new Error(dataStr || 'Failed to load stock report');
          } else if (eventType === 'done') {
            setIsAnalyzing(false);
          }
        }
      }
    } catch (e) {
      setStockReport({ error: 'Failed to load stock report' });
    } finally {
      setIsLoadingReport(false);
      setIsAnalyzing(false);
    }
  };

  const closeReportModal = () => {
    setShowReportModal(false);
    setStockReport(null);
    setIsLoadingReport(false);
    setIsAnalyzing(false);
  };

  const handleSearchClick = () => {
    setShowSearchInput(true);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 50);
  };

  const searchStocks = async searchTerm => {
    setIsSearching(true);
    try {
      const response = await fetch(
        `/api/search-stocks?key=${encodeURIComponent(searchTerm)}`
      );

      if (response.ok) {
        const results = await response.json();
        setSearchResults(results);
        setShowSearchResultsModal(true);
        return;
      } else if (response.status === 404) {
        alert('검색 결과가 없습니다.');
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      let errorMessage = '검색 중 오류가 발생했습니다';
      if (
        error.name === 'TypeError' &&
        error.message.includes('Failed to fetch')
      ) {
        errorMessage =
          'CORS 정책으로 인해 KRX API 서버에 접근할 수 없습니다. 서버 설정을 확인해주세요.';
      } else if (error.message.includes('HTTP error')) {
        errorMessage = `서버 오류가 발생했습니다: ${error.message}`;
      } else {
        errorMessage = `검색 중 오류가 발생했습니다: ${error.message}`;
      }
      alert(errorMessage);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchSubmit = async e => {
    e.preventDefault();
    if (searchTicker.trim()) {
      const searchTerm = searchTicker.trim();

      if (networkStatus === 'offline') {
        alert('네트워크 연결이 없습니다. 인터넷 연결을 확인해주세요.');
        return;
      }

      if (/^\d{6}$/.test(searchTerm)) {
        fetchStockReport(searchTerm.toUpperCase());
        setSearchTicker('');
        setShowSearchInput(false);
      } else {
        await searchStocks(searchTerm);
      }
    }
  };

  const handleStockSelection = ticker => {
    fetchStockReport(ticker);
    setShowSearchResultsModal(false);
    setSearchTicker('');
    setShowSearchInput(false);
  };

  const closeSearchResultsModal = () => {
    setShowSearchResultsModal(false);
    setSearchResults([]);
  };

  const handleSearchCancel = () => {
    setShowSearchInput(false);
    setSearchTicker('');
  };

  const handleAddInvestmentClick = () => {
    setShowAddInvestmentModal(true);
  };

  const handleAddInvestmentCancel = () => {
    setShowAddInvestmentModal(false);
    setAddInvestmentForm({
      ticker: '',
      budget: '1000000',
      exit_condition: '10',
      step_trigger_percentages: [0.0, -3.0, -5.0, -10.0, -20.0, -40.0, -70.0],
      auto_restart: false,
    });
    setOtpToken(null);
    setOtpCode('');
    setShowOtpInput(false);
    setOtpExpiresIn(0);
    setShowConfirmAddInvestment(false);
    setConfirmAddInvestmentData(null);
  };

  const handleDetailBalanceClick = () => {
    setShowDetailBalanceModal(true);
  };

  const handleDetailBalanceCancel = () => {
    setShowDetailBalanceModal(false);
    setDetailBalanceData(null);
    setDetailBalanceOtpToken(null);
    setDetailBalanceOtpCode('');
    setShowDetailBalanceOtpInput(false);
    setDetailBalanceOtpExpiresIn(0);
  };

  const handleDetailBalanceSubmit = async e => {
    e.preventDefault();
    setIsLoadingDetailBalance(true);
    try {
      const response = await fetch('/api/detail-balance');
      if (!response.ok) {
        let errorMessage = 'Failed to request OTP';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setDetailBalanceOtpToken(result.otp_token);
      setDetailBalanceOtpExpiresIn(result.expires_in);
      setShowDetailBalanceOtpInput(true);
    } catch (error) {
      alert(`Failed to request OTP: ${error.message}`);
    } finally {
      setIsLoadingDetailBalance(false);
    }
  };

  const handleDetailBalanceOtpSubmit = async e => {
    e.preventDefault();
    if (
      !detailBalanceOtpCode.trim() ||
      !/^\d{6}$/.test(detailBalanceOtpCode.trim())
    ) {
      alert('Please enter a valid 6-digit OTP code');
      return;
    }

    setIsLoadingDetailBalance(true);
    try {
      const response = await fetch('/api/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          otp: detailBalanceOtpCode.trim(),
          otp_token: detailBalanceOtpToken,
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to verify OTP';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (result.action === 'detail_balance' && result.data) {
        setDetailBalanceData(result.data);
        setShowDetailBalanceOtpInput(false);
      } else {
        throw new Error('Invalid response action or missing data');
      }
    } catch (error) {
      alert(`Failed to verify OTP: ${error.message}`);
    } finally {
      setIsLoadingDetailBalance(false);
    }
  };

  const handleDetailBalanceOtpInputChange = e => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setDetailBalanceOtpCode(value);
    }
  };

  const handleDeleteInvestmentClick = async ticker => {
    if (
      !window.confirm(
        `정말로 ${ticker} 투자를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`
      )
    ) {
      return;
    }

    setTickerToDelete(ticker);
    setIsDeletingInvestment(true);
    try {
      const response = await fetch(`/api/investment/${ticker}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        let errorMessage = 'Failed to request OTP';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setDeleteOtpToken(result.otp_token);
      setDeleteOtpExpiresIn(result.expires_in);
      setShowDeleteOtpInput(true);
    } catch (error) {
      alert(`Failed to request OTP: ${error.message}`);
    } finally {
      setIsDeletingInvestment(false);
    }
  };

  const handleDeleteOtpSubmit = async e => {
    e.preventDefault();
    if (!deleteOtpCode.trim() || !/^\d{6}$/.test(deleteOtpCode.trim())) {
      alert('Please enter a valid 6-digit OTP code');
      return;
    }

    setIsDeletingInvestment(true);
    try {
      const response = await fetch('/api/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          otp: deleteOtpCode.trim(),
          otp_token: deleteOtpToken,
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to verify OTP';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      alert(result.message || 'Investment deleted successfully');
      handleDeleteCancel();
      fetchData();
    } catch (error) {
      alert(`Failed to verify OTP: ${error.message}`);
    } finally {
      setIsDeletingInvestment(false);
    }
  };

  const handleDeleteOtpInputChange = e => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setDeleteOtpCode(value);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteOtpInput(false);
    setDeleteOtpToken(null);
    setDeleteOtpCode('');
    setDeleteOtpExpiresIn(0);
    setTickerToDelete('');
  };

  const handleUpdateSettingsClick = (
    ticker,
    currentExitCondition,
    currentAutoRestart
  ) => {
    setUpdateSettingsForm({
      ticker: ticker,
      exit_condition: currentExitCondition.toString(),
      auto_restart: currentAutoRestart,
    });
    setShowUpdateSettingsModal(true);
  };

  const handleUpdateSettingsCancel = () => {
    setShowUpdateSettingsModal(false);
    setUpdateSettingsForm({
      ticker: '',
      exit_condition: '',
      auto_restart: false,
    });
    setUpdateSettingsOtpToken(null);
    setUpdateSettingsOtpCode('');
    setShowUpdateSettingsOtpInput(false);
    setUpdateSettingsOtpExpiresIn(0);
  };

  const handleUpdateSettingsSubmit = async e => {
    e.preventDefault();
    if (!updateSettingsForm.exit_condition) {
      alert('Please enter exit condition');
      return;
    }

    setIsUpdatingSettings(true);
    try {
      const response = await fetch(
        `/api/investment/${updateSettingsForm.ticker}/settings`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            exit_condition: parseInt(updateSettingsForm.exit_condition),
            auto_restart: updateSettingsForm.auto_restart,
          }),
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to request OTP';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setUpdateSettingsOtpToken(result.otp_token);
      setUpdateSettingsOtpExpiresIn(result.expires_in);
      setShowUpdateSettingsOtpInput(true);
    } catch (error) {
      alert(`Failed to request OTP: ${error.message}`);
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  const handleUpdateSettingsOtpSubmit = async e => {
    e.preventDefault();
    if (
      !updateSettingsOtpCode.trim() ||
      !/^\d{6}$/.test(updateSettingsOtpCode.trim())
    ) {
      alert('Please enter a valid 6-digit OTP code');
      return;
    }

    setIsUpdatingSettings(true);
    try {
      const response = await fetch('/api/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          otp: updateSettingsOtpCode.trim(),
          otp_token: updateSettingsOtpToken,
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to verify OTP';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      alert(result.message || 'Settings updated successfully');
      handleUpdateSettingsCancel();
      fetchData();
    } catch (error) {
      alert(`Failed to verify OTP: ${error.message}`);
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  const handleUpdateSettingsOtpInputChange = e => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setUpdateSettingsOtpCode(value);
    }
  };

  const handleUpdateSettingsInputChange = e => {
    const { name, value, type, checked } = e.target;
    setUpdateSettingsForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAddInvestmentSubmit = async e => {
    e.preventDefault();

    if (
      !addInvestmentForm.ticker.trim() ||
      !addInvestmentForm.budget ||
      !addInvestmentForm.exit_condition
    ) {
      alert('Please fill in all fields');
      return;
    }

    const validPercentages = addInvestmentForm.step_trigger_percentages
      .filter(val => val !== '')
      .map(val => parseFloat(val));

    if (validPercentages.length === 0) {
      alert('Please enter at least one step trigger percentage');
      return;
    }

    if (validPercentages.some(val => isNaN(val) || val < -100 || val > 0)) {
      alert('Please enter valid percentages between -100 and 0');
      return;
    }

    setIsAddingInvestment(true);
    try {
      const infoRes = await fetch(
        `/api/stock-info?ticker=${addInvestmentForm.ticker.trim().toUpperCase()}`
      );
      if (!infoRes.ok) {
        throw new Error('종목 정보를 가져오는데 실패했습니다.');
      }
      const stockInfo = await infoRes.json();

      const estimatedShares = Math.floor(
        Number(addInvestmentForm.budget) / Number(stockInfo.price)
      );

      if (estimatedShares < 4) {
        alert(
          `예상 매수 가능 수량이 ${estimatedShares}주로 4주 미만입니다. 예산을 늘리거나 다른 종목을 선택해주세요.`
        );
        return;
      }

      setConfirmAddInvestmentData({
        ...addInvestmentForm,
        name: stockInfo.name,
        price: stockInfo.price,
      });
      setShowConfirmAddInvestment(true);
      setShowAddInvestmentModal(false);
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setIsAddingInvestment(false);
    }
  };

  const proceedAddInvestment = async () => {
    setIsAddingInvestment(true);
    try {
      const response = await fetch('/api/add-investment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner: data.owner,
          ticker: confirmAddInvestmentData.ticker.trim().toUpperCase(),
          budget: parseInt(confirmAddInvestmentData.budget),
          exit_condition: parseInt(confirmAddInvestmentData.exit_condition),
          step_trigger_percentages:
            confirmAddInvestmentData.step_trigger_percentages
              .filter(val => val !== '')
              .map(val => parseFloat(val)),
          auto_restart: confirmAddInvestmentData.auto_restart,
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to request OTP';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setOtpToken(result.otp_token);
      setOtpExpiresIn(result.expires_in);
      setShowOtpInput(true);
      setShowConfirmAddInvestment(false);
      setShowAddInvestmentModal(true);
    } catch (error) {
      alert(`Failed to request OTP: ${error.message}`);
    } finally {
      setIsAddingInvestment(false);
    }
  };

  const handleOtpSubmit = async e => {
    e.preventDefault();
    if (!otpCode.trim() || !/^\d{6}$/.test(otpCode.trim())) {
      alert('Please enter a valid 6-digit OTP code');
      return;
    }

    setIsAddingInvestment(true);
    try {
      const response = await fetch('/api/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          otp: otpCode.trim(),
          otp_token: otpToken,
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to verify OTP';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      alert(result.message || 'Investment added successfully');
      handleAddInvestmentCancel();
      fetchData();
    } catch (error) {
      alert(`Failed to verify OTP: ${error.message}`);
    } finally {
      setIsAddingInvestment(false);
    }
  };

  const handleOtpInputChange = e => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setOtpCode(value);
    }
  };

  const handleAddInvestmentInputChange = e => {
    const { name, value } = e.target;
    if (name.startsWith('step_trigger_percentages_')) {
      const index = parseInt(name.split('_')[3]);
      setAddInvestmentForm(prev => ({
        ...prev,
        step_trigger_percentages: prev.step_trigger_percentages.map(
          (item, i) => (i === index ? value : item)
        ),
      }));
    } else {
      setAddInvestmentForm(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  useEffect(() => {
    fetchData();

    const eventSource = new EventSource('/api/events');

    eventSource.addEventListener('data', event => {
      try {
        const payload = JSON.parse(event.data);
        setData(payload);
        setError(null);
      } catch (err) {
        // SSE parse error ignored
      }
    });

    eventSource.addEventListener('logs', event => {
      try {
        const payload = JSON.parse(event.data);
        setLogs(payload);
      } catch (err) {
        // SSE parse error ignored
      }
    });

    eventSource.onerror = () => {
      if (!intervalRef.current) {
        intervalRef.current = setInterval(() => fetchData(), 5000);
      }
    };

    return () => {
      eventSource.close();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchData]);

  // Online / Offline Monitor
  useEffect(() => {
    const handleOnline = () => setNetworkStatus('online');
    const handleOffline = () => setNetworkStatus('offline');

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    setNetworkStatus(navigator.onLine ? 'online' : 'offline');

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // OTP countdown timer
  useEffect(() => {
    if (otpExpiresIn > 0) {
      const timer = setInterval(() => {
        setOtpExpiresIn(prev => {
          if (prev <= 1) {
            if (showOtpInput && !isAddingInvestment) {
              handleAddInvestmentCancel();
              alert('OTP has expired. Please try again.');
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [otpExpiresIn, showOtpInput, isAddingInvestment]);

  // Detail Balance OTP countdown timer
  useEffect(() => {
    if (detailBalanceOtpExpiresIn > 0) {
      const timer = setInterval(() => {
        setDetailBalanceOtpExpiresIn(prev => {
          if (prev <= 1) {
            if (showDetailBalanceOtpInput && !isLoadingDetailBalance) {
              handleDetailBalanceCancel();
              alert('OTP has expired. Please try again.');
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [
    detailBalanceOtpExpiresIn,
    showDetailBalanceOtpInput,
    isLoadingDetailBalance,
  ]);

  // Delete OTP countdown timer
  useEffect(() => {
    if (deleteOtpExpiresIn > 0) {
      const timer = setInterval(() => {
        setDeleteOtpExpiresIn(prev => {
          if (prev <= 1) {
            if (showDeleteOtpInput && !isDeletingInvestment) {
              handleDeleteCancel();
              alert('OTP has expired. Please try again.');
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [deleteOtpExpiresIn, showDeleteOtpInput, isDeletingInvestment]);

  // Update Settings OTP countdown timer
  useEffect(() => {
    if (updateSettingsOtpExpiresIn > 0) {
      const timer = setInterval(() => {
        setUpdateSettingsOtpExpiresIn(prev => {
          if (prev <= 1) {
            if (showUpdateSettingsOtpInput && !isUpdatingSettings) {
              handleUpdateSettingsCancel();
              alert('OTP has expired. Please try again.');
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [
    updateSettingsOtpExpiresIn,
    showUpdateSettingsOtpInput,
    isUpdatingSettings,
  ]);

  // Global Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = e => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearchInput(true);
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') {
        if (showSearchInput) handleSearchCancel();
        if (showAddInvestmentModal) handleAddInvestmentCancel();
        if (showDetailBalanceModal) handleDetailBalanceCancel();
        if (showDeleteOtpInput) handleDeleteCancel();
        if (showReportModal) closeReportModal();
        if (showSearchResultsModal) closeSearchResultsModal();
        if (showUpdateSettingsModal) handleUpdateSettingsCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    showSearchInput,
    showAddInvestmentModal,
    showDetailBalanceModal,
    showDeleteOtpInput,
    showReportModal,
    showSearchResultsModal,
    showUpdateSettingsModal,
  ]);

  if (error) {
    return (
      <div className="app-error-screen">
        <div className="error-card">
          <div className="error-icon-wrap">
            <WarningCircle size={40} weight="duotone" />
          </div>
          <h2>Connection Error</h2>
          <p className="error-message">{error}</p>
          <div className="error-actions">
            <button onClick={() => fetchData()} className="btn-primary">
              <ArrowsClockwise size={16} weight="bold" />
              Retry Connection
            </button>
            <button
              onClick={() => window.location.reload()}
              className="btn-secondary"
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="app-loading-screen">
        <div className="loading-indicator">
          <div className="spinner-ring"></div>
          <span>Loading 7-Split Investment System...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      {/* Top Navbar */}
      <header className="app-navbar">
        <div className="navbar-container">
          <div className="brand-group">
            <div className="brand-badge">7-SPLIT</div>
            <h1 className="brand-title">
              {data.owner ? `${data.owner}'s ` : ''}Split Dashboard
            </h1>
          </div>

          <div className="navbar-controls">
            {showSearchInput ? (
              <form
                onSubmit={handleSearchSubmit}
                className="inline-search-form"
              >
                <MagnifyingGlass
                  size={16}
                  weight="bold"
                  className="search-prefix-icon"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTicker}
                  onChange={e => setSearchTicker(e.target.value)}
                  placeholder="Ticker (005930) or company name..."
                  className="inline-search-input"
                />
                <button
                  type="button"
                  onClick={handleSearchCancel}
                  className="search-cancel-btn"
                  title="Close search (Esc)"
                >
                  <X size={14} weight="bold" />
                </button>
              </form>
            ) : (
              <div className="toolbar-actions">
                <div className="sort-dropdown-wrap" title="Sort investments">
                  <SlidersHorizontal
                    size={14}
                    weight="bold"
                    className="sort-icon"
                  />
                  <select
                    value={sortBy}
                    onChange={e => handleSortChange(e.target.value)}
                    className="sort-dropdown"
                  >
                    <option value="default">Sort: Chance</option>
                    <option value="name">Sort: Name</option>
                    <option value="profit">Sort: Profit</option>
                  </select>
                </div>

                <button
                  onClick={handleSearchClick}
                  className="toolbar-btn"
                  title="Search stock or run AI analysis (Ctrl+K)"
                  aria-label="Search"
                >
                  <MagnifyingGlass size={18} weight="bold" />
                </button>

                <button
                  onClick={handleDetailBalanceClick}
                  className="toolbar-btn"
                  title="Account Balance Details (OTP)"
                  aria-label="Account Balance"
                >
                  <Bank size={18} weight="bold" />
                </button>

                <button
                  onClick={handleAddInvestmentClick}
                  className="toolbar-btn highlight"
                  title="Add new investment target"
                  aria-label="Add Investment"
                >
                  <Plus size={18} weight="bold" />
                  <span className="btn-text-desktop">New Target</span>
                </button>

                <button
                  onClick={toggleTheme}
                  className="toolbar-btn"
                  title="Toggle Light/Dark Theme"
                  aria-label="Toggle Theme"
                >
                  {theme === 'light' ? (
                    <Moon size={18} weight="bold" />
                  ) : (
                    <Sun size={18} weight="bold" />
                  )}
                </button>

                <div
                  className={`status-pill ${networkStatus}`}
                  title={`Network: ${networkStatus}`}
                >
                  <div className="status-dot"></div>
                  <span className="status-text">{networkStatus}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="app-main-content">
        <GlobalInfo data={data} />
        <InvestmentsTable
          investments={data.investments}
          onStockTitleClick={fetchStockReport}
          onDeleteClick={handleDeleteInvestmentClick}
          onUpdateSettingsClick={handleUpdateSettingsClick}
        />
        <LogsContainer logs={logs} />
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <div className="footer-content">
          <span>
            &copy; Homin Lee &lt;
            <a href="mailto:i@homin.dev">i@homin.dev</a>&gt; All rights
            reserved.
          </span>
          <span className="footer-tag">
            7-Split Automated Value-Investment Terminal
          </span>
        </div>
      </footer>

      {/* Stock Report Modal */}
      {showReportModal && (
        <div
          className="modal-overlay"
          onClick={closeReportModal}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') closeReportModal();
          }}
          role="button"
          tabIndex="0"
          aria-label="Close modal"
        >
          <div
            className="modal-dialog large"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            role="presentation"
          >
            <div className="modal-header">
              <div className="modal-title-with-icon">
                <Sparkle size={20} weight="fill" className="ai-icon" />
                <h2>AI Stock Analysis & Checklist</h2>
              </div>
              <button
                className="modal-close-btn"
                onClick={closeReportModal}
                aria-label="Close"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="modal-body">
              {isLoadingReport ? (
                <div className="report-loading-box">
                  <div className="spinner-ring"></div>
                  <p>
                    Fetching financial data & calculating 7-Split checklist...
                  </p>
                </div>
              ) : stockReport ? (
                <div className="stock-report-content">
                  {stockReport.error ? (
                    <div className="report-error-box">
                      <WarningCircle size={20} weight="bold" />
                      <span>{stockReport.error}</span>
                    </div>
                  ) : (
                    <>
                      {stockReport.analysis ? (
                        <div className="report-card">
                          <div className="report-card-header">
                            <Sparkle size={16} weight="fill" />
                            <h3>AI Valuation & Financial Summary</h3>
                          </div>
                          <div className="markdown-body">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {stockReport.analysis}
                            </ReactMarkdown>
                          </div>
                        </div>
                      ) : isAnalyzing ? (
                        <div className="report-card analyzing">
                          <div className="report-loading-box mini">
                            <div className="spinner-ring small"></div>
                            <p>AI is generating deep investment insight...</p>
                          </div>
                        </div>
                      ) : null}

                      {stockReport.check_list && (
                        <div className="report-card">
                          <div className="report-card-header">
                            <CheckCircle size={16} weight="fill" />
                            <h3>Investment Criteria Checklist</h3>
                          </div>
                          <div className="markdown-body">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {stockReport.check_list}
                            </ReactMarkdown>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Add Investment Modal */}
      {showConfirmAddInvestment && confirmAddInvestmentData && (
        <div
          className="modal-overlay"
          onClick={handleAddInvestmentCancel}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') handleAddInvestmentCancel();
          }}
          role="button"
          tabIndex="0"
          aria-label="Close modal"
        >
          <div
            className="modal-dialog"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            role="presentation"
          >
            <div className="modal-header">
              <div className="modal-title-with-icon">
                <CheckCircle size={20} weight="bold" />
                <h2>Confirm Target & Order</h2>
              </div>
              <button
                className="modal-close-btn"
                onClick={handleAddInvestmentCancel}
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="modal-body">
              <div className="confirm-summary-card">
                <div className="summary-row">
                  <span className="summary-label">Target Stock</span>
                  <span className="summary-val font-bold">
                    {confirmAddInvestmentData.name} (
                    {confirmAddInvestmentData.ticker})
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Current Market Price</span>
                  <span className="summary-val mono">
                    {Number(confirmAddInvestmentData.price).toLocaleString()}{' '}
                    KRW
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Total Allocated Budget</span>
                  <span className="summary-val mono text-primary-accent font-bold">
                    {Number(confirmAddInvestmentData.budget).toLocaleString()}{' '}
                    KRW
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Est. Buyable Quantity</span>
                  <span className="summary-val mono">
                    {Math.floor(
                      Number(confirmAddInvestmentData.budget) /
                        Number(confirmAddInvestmentData.price)
                    )}{' '}
                    Shares
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Exit Profit Target</span>
                  <span className="summary-val mono text-success font-bold">
                    +{confirmAddInvestmentData.exit_condition}%
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Auto-Restart Strategy</span>
                  <span className="summary-val">
                    {confirmAddInvestmentData.auto_restart
                      ? 'Enabled'
                      : 'Disabled'}
                  </span>
                </div>
              </div>

              <div className="modal-footer-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowConfirmAddInvestment(false);
                    setShowAddInvestmentModal(true);
                  }}
                  className="btn-secondary"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={proceedAddInvestment}
                  className="btn-primary"
                  disabled={isAddingInvestment}
                >
                  {isAddingInvestment ? (
                    <span className="btn-loading">
                      <span className="spinner-ring mini"></span> Requesting
                      OTP...
                    </span>
                  ) : (
                    'Confirm & Request OTP'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Investment Modal */}
      {showAddInvestmentModal && (
        <div
          className="modal-overlay"
          onClick={handleAddInvestmentCancel}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') handleAddInvestmentCancel();
          }}
          role="button"
          tabIndex="0"
          aria-label="Close modal"
        >
          <div
            className="modal-dialog"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            role="presentation"
          >
            <div className="modal-header">
              <div className="modal-title-with-icon">
                <Plus size={20} weight="bold" />
                <h2>Add Investment Strategy</h2>
              </div>
              <button
                className="modal-close-btn"
                onClick={handleAddInvestmentCancel}
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="modal-body">
              {!showOtpInput ? (
                <form
                  onSubmit={handleAddInvestmentSubmit}
                  className="modern-form"
                >
                  <div className="form-group">
                    <label htmlFor="ticker">Stock Ticker Symbol</label>
                    <input
                      type="text"
                      id="ticker"
                      name="ticker"
                      value={addInvestmentForm.ticker}
                      onChange={handleAddInvestmentInputChange}
                      placeholder="e.g. 005930"
                      className="form-input mono"
                      required
                    />
                  </div>

                  <div className="form-row-2col">
                    <div className="form-group">
                      <label htmlFor="budget">Total Budget (KRW)</label>
                      <input
                        type="number"
                        id="budget"
                        name="budget"
                        value={addInvestmentForm.budget}
                        onChange={handleAddInvestmentInputChange}
                        placeholder="e.g. 1000000"
                        className="form-input mono"
                        min="1"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="exit_condition">Exit Profit (%)</label>
                      <input
                        type="number"
                        id="exit_condition"
                        name="exit_condition"
                        value={addInvestmentForm.exit_condition}
                        onChange={handleAddInvestmentInputChange}
                        placeholder="e.g. 15"
                        className="form-input mono"
                        min="10"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <span className="form-label-heading">
                      Step Dip Trigger (%)
                    </span>
                    <div className="trigger-percentages-bar">
                      {[0, 1, 2, 3, 4, 5, 6].map(index => (
                        <div key={index} className="trigger-percentage-slot">
                          <span className="slot-step-label">S{index + 1}</span>
                          <input
                            type="number"
                            name={`step_trigger_percentages_${index}`}
                            value={
                              addInvestmentForm.step_trigger_percentages[index]
                            }
                            onChange={handleAddInvestmentInputChange}
                            placeholder="0.0"
                            step="0.1"
                            min="-100"
                            max="0"
                            disabled={index === 0}
                            className={`slot-input mono ${index === 0 ? 'disabled' : ''}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-checkbox-card">
                    <label className="checkbox-custom-label">
                      <input
                        type="checkbox"
                        name="auto_restart"
                        checked={addInvestmentForm.auto_restart}
                        onChange={e =>
                          setAddInvestmentForm(prev => ({
                            ...prev,
                            auto_restart: e.target.checked,
                          }))
                        }
                      />
                      <span className="checkbox-title">
                        Auto Restart (자동 재시작)
                      </span>
                    </label>
                    <p className="checkbox-help">
                      Automatically re-initiates Step 1 once all 7 steps have
                      successfully exited.
                    </p>
                  </div>

                  <div className="modal-footer-actions">
                    <button
                      type="button"
                      onClick={handleAddInvestmentCancel}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={isAddingInvestment}
                    >
                      {isAddingInvestment ? (
                        <span className="btn-loading">
                          <span className="spinner-ring mini"></span>{' '}
                          Validating...
                        </span>
                      ) : (
                        'Next: Review & OTP'
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleOtpSubmit} className="modern-form">
                  <div className="otp-verification-card">
                    <div className="otp-icon-wrap">
                      <ShieldCheck size={32} weight="duotone" />
                    </div>
                    <h3>Security Authorization</h3>
                    <p>
                      Enter the 6-digit OTP code sent to your authentication
                      device.
                    </p>

                    <div className="otp-input-wrap">
                      <input
                        type="text"
                        name="otp"
                        value={otpCode}
                        onChange={handleOtpInputChange}
                        placeholder="000000"
                        maxLength="6"
                        required
                        inputMode="numeric"
                        pattern="\d{6}"
                        className="otp-code-input mono"
                      />
                    </div>

                    <div
                      className={`otp-timer-badge ${
                        otpExpiresIn <= 30 ? 'warning' : ''
                      }`}
                    >
                      {otpExpiresIn > 0
                        ? `Expires in ${Math.floor(otpExpiresIn / 60)}:${(
                            otpExpiresIn % 60
                          )
                            .toString()
                            .padStart(2, '0')}`
                        : 'OTP Expired'}
                    </div>
                  </div>

                  <div className="modal-footer-actions">
                    <button
                      type="button"
                      onClick={handleAddInvestmentCancel}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={isAddingInvestment}
                    >
                      {isAddingInvestment ? (
                        <span className="btn-loading">
                          <span className="spinner-ring mini"></span>{' '}
                          Verifying...
                        </span>
                      ) : (
                        'Verify & Start Strategy'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail Balance Modal */}
      {showDetailBalanceModal && (
        <div
          className="modal-overlay"
          onClick={handleDetailBalanceCancel}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') handleDetailBalanceCancel();
          }}
          role="button"
          tabIndex="0"
          aria-label="Close modal"
        >
          <div
            className="modal-dialog large"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            role="presentation"
          >
            <div className="modal-header">
              <div className="modal-title-with-icon">
                <Bank size={20} weight="bold" />
                <h2>Account Portfolio & Balance</h2>
              </div>
              <button
                className="modal-close-btn"
                onClick={handleDetailBalanceCancel}
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="modal-body">
              {!showDetailBalanceOtpInput ? (
                <div className="detail-balance-wrap">
                  {detailBalanceData ? (
                    <div className="balance-categories-grid">
                      {/* CMA & Deposit */}
                      <div className="balance-category-card">
                        <div className="category-header">
                          <span className="category-title">Cash & CMA</span>
                          {detailBalanceData.deposit_cma?.total_ratio !=
                            null && (
                            <span className="category-ratio-pill mono">
                              {detailBalanceData.deposit_cma.total_ratio.toFixed(
                                1
                              )}
                              % Total
                            </span>
                          )}
                        </div>
                        <div className="category-metrics-list">
                          {detailBalanceData.deposit_cma?.evaluation_amount !=
                            null && (
                            <div className="cat-metric-item">
                              <span className="metric-name">Evaluation</span>
                              <span className="metric-val mono font-bold">
                                {detailBalanceData.deposit_cma.evaluation_amount.toLocaleString()}{' '}
                                KRW
                              </span>
                            </div>
                          )}
                          {detailBalanceData.deposit_cma?.realized_pnl_amount !=
                            null && (
                            <div className="cat-metric-item">
                              <span className="metric-name">Realized PnL</span>
                              <span className="metric-val mono">
                                {detailBalanceData.deposit_cma.realized_pnl_amount.toLocaleString()}{' '}
                                KRW
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Stocks */}
                      <div className="balance-category-card">
                        <div className="category-header">
                          <span className="category-title">
                            Equities & Stocks
                          </span>
                          {detailBalanceData.stock?.total_ratio != null && (
                            <span className="category-ratio-pill mono">
                              {detailBalanceData.stock.total_ratio.toFixed(1)}%
                              Total
                            </span>
                          )}
                        </div>
                        <div className="category-metrics-list">
                          {detailBalanceData.stock?.purchase_amount != null && (
                            <div className="cat-metric-item">
                              <span className="metric-name">Cost Basis</span>
                              <span className="metric-val mono">
                                {detailBalanceData.stock.purchase_amount.toLocaleString()}{' '}
                                KRW
                              </span>
                            </div>
                          )}
                          {detailBalanceData.stock?.evaluation_amount !=
                            null && (
                            <div className="cat-metric-item">
                              <span className="metric-name">Evaluation</span>
                              <span className="metric-val mono font-bold">
                                {detailBalanceData.stock.evaluation_amount.toLocaleString()}{' '}
                                KRW
                              </span>
                            </div>
                          )}
                          {detailBalanceData.stock?.evaluation_pnl_amount !=
                            null && (
                            <div className="cat-metric-item">
                              <span className="metric-name">
                                Unrealized PnL
                              </span>
                              <span
                                className={`metric-val mono ${
                                  detailBalanceData.stock
                                    .evaluation_pnl_amount > 0
                                    ? 'text-success'
                                    : detailBalanceData.stock
                                          .evaluation_pnl_amount < 0
                                      ? 'text-danger'
                                      : ''
                                }`}
                              >
                                {detailBalanceData.stock.evaluation_pnl_amount >
                                0
                                  ? '+'
                                  : ''}
                                {detailBalanceData.stock.evaluation_pnl_amount.toLocaleString()}{' '}
                                KRW
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Total Portfolio Summary */}
                      <div className="balance-category-card total-summary">
                        <div className="category-header">
                          <span className="category-title">
                            Total Account Balance
                          </span>
                          <span className="category-badge">Unified</span>
                        </div>
                        <div className="category-metrics-list">
                          {detailBalanceData.total?.evaluation_amount !=
                            null && (
                            <div className="cat-metric-item highlight">
                              <span className="metric-name">
                                Net Asset Value
                              </span>
                              <span className="metric-val mono font-bold text-lg">
                                {detailBalanceData.total.evaluation_amount.toLocaleString()}{' '}
                                KRW
                              </span>
                            </div>
                          )}
                          {detailBalanceData.total?.evaluation_pnl_amount !=
                            null && (
                            <div className="cat-metric-item">
                              <span className="metric-name">
                                Total Unrealized PnL
                              </span>
                              <span
                                className={`metric-val mono font-bold ${
                                  detailBalanceData.total
                                    .evaluation_pnl_amount > 0
                                    ? 'text-success'
                                    : detailBalanceData.total
                                          .evaluation_pnl_amount < 0
                                      ? 'text-danger'
                                      : ''
                                }`}
                              >
                                {detailBalanceData.total.evaluation_pnl_amount >
                                0
                                  ? '+'
                                  : ''}
                                {detailBalanceData.total.evaluation_pnl_amount.toLocaleString()}{' '}
                                KRW
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="request-auth-prompt">
                      <div className="auth-icon-wrap">
                        <Bank size={36} weight="duotone" />
                      </div>
                      <h3>Brokerage Account Verification</h3>
                      <p>
                        Viewing synchronized account valuation and CMA breakdown
                        requires OTP authorization.
                      </p>
                      <form
                        onSubmit={handleDetailBalanceSubmit}
                        className="auth-form"
                      >
                        <div className="modal-footer-actions center">
                          <button
                            type="button"
                            onClick={handleDetailBalanceCancel}
                            className="btn-secondary"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn-primary"
                            disabled={isLoadingDetailBalance}
                          >
                            {isLoadingDetailBalance ? (
                              <span className="btn-loading">
                                <span className="spinner-ring mini"></span>{' '}
                                Requesting...
                              </span>
                            ) : (
                              'Request Balance OTP'
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              ) : (
                <form
                  onSubmit={handleDetailBalanceOtpSubmit}
                  className="modern-form"
                >
                  <div className="otp-verification-card">
                    <div className="otp-icon-wrap">
                      <ShieldCheck size={32} weight="duotone" />
                    </div>
                    <h3>Account Balance OTP</h3>
                    <p>
                      Enter the 6-digit OTP code to unlock your portfolio
                      overview.
                    </p>

                    <div className="otp-input-wrap">
                      <input
                        type="text"
                        name="otp"
                        value={detailBalanceOtpCode}
                        onChange={handleDetailBalanceOtpInputChange}
                        placeholder="000000"
                        maxLength="6"
                        required
                        inputMode="numeric"
                        pattern="\d{6}"
                        className="otp-code-input mono"
                      />
                    </div>

                    <div
                      className={`otp-timer-badge ${
                        detailBalanceOtpExpiresIn <= 30 ? 'warning' : ''
                      }`}
                    >
                      {detailBalanceOtpExpiresIn > 0
                        ? `Expires in ${Math.floor(detailBalanceOtpExpiresIn / 60)}:${(
                            detailBalanceOtpExpiresIn % 60
                          )
                            .toString()
                            .padStart(2, '0')}`
                        : 'OTP Expired'}
                    </div>
                  </div>

                  <div className="modal-footer-actions">
                    <button
                      type="button"
                      onClick={handleDetailBalanceCancel}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={isLoadingDetailBalance}
                    >
                      {isLoadingDetailBalance ? (
                        <span className="btn-loading">
                          <span className="spinner-ring mini"></span>{' '}
                          Unlocking...
                        </span>
                      ) : (
                        'Verify & View Balance'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search Results Modal */}
      {showSearchResultsModal && (
        <div
          className="modal-overlay"
          onClick={closeSearchResultsModal}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') closeSearchResultsModal();
          }}
          role="button"
          tabIndex="0"
          aria-label="Close modal"
        >
          <div
            className="modal-dialog"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            role="presentation"
          >
            <div className="modal-header">
              <div className="modal-title-with-icon">
                <MagnifyingGlass size={20} weight="bold" />
                <h2>Stock Search Results</h2>
              </div>
              <button
                className="modal-close-btn"
                onClick={closeSearchResultsModal}
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="modal-body">
              {isSearching ? (
                <div className="report-loading-box">
                  <div className="spinner-ring"></div>
                  <p>Searching KRX markets...</p>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="empty-results-box">
                  <p>
                    No matching stocks found for &quot;{searchTicker}&quot;.
                  </p>
                </div>
              ) : (
                <div className="search-results-list">
                  {searchResults.map(stock => (
                    <div
                      key={stock.ticker}
                      className="search-item-row"
                      onClick={() => handleStockSelection(stock.ticker)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleStockSelection(stock.ticker);
                        }
                      }}
                      role="button"
                      tabIndex="0"
                    >
                      <div className="search-stock-meta">
                        <span className="search-stock-name">{stock.name}</span>
                        <span className="search-stock-ticker mono">
                          {stock.ticker}
                        </span>
                      </div>
                      <span className="search-stock-market">
                        {stock.market}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Update Investment Settings Modal */}
      {showUpdateSettingsModal && (
        <div
          className="modal-overlay"
          onClick={handleUpdateSettingsCancel}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ')
              handleUpdateSettingsCancel();
          }}
          role="button"
          tabIndex="0"
          aria-label="Close modal"
        >
          <div
            className="modal-dialog"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            role="presentation"
          >
            <div className="modal-header">
              <div className="modal-title-with-icon">
                <GearSix size={20} weight="bold" />
                <h2>Update Settings: {updateSettingsForm.ticker}</h2>
              </div>
              <button
                className="modal-close-btn"
                onClick={handleUpdateSettingsCancel}
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="modal-body">
              {!showUpdateSettingsOtpInput ? (
                <form
                  onSubmit={handleUpdateSettingsSubmit}
                  className="modern-form"
                >
                  <div className="form-group">
                    <label htmlFor="update_exit_condition">
                      Exit Profit Target (%)
                    </label>
                    <input
                      type="number"
                      id="update_exit_condition"
                      name="exit_condition"
                      value={updateSettingsForm.exit_condition}
                      onChange={handleUpdateSettingsInputChange}
                      placeholder="e.g. 15"
                      className="form-input mono"
                      min="10"
                      required
                    />
                  </div>

                  <div className="form-checkbox-card">
                    <label className="checkbox-custom-label">
                      <input
                        type="checkbox"
                        name="auto_restart"
                        checked={updateSettingsForm.auto_restart}
                        onChange={handleUpdateSettingsInputChange}
                      />
                      <span className="checkbox-title">
                        Auto Restart (자동 재시작)
                      </span>
                    </label>
                    <p className="checkbox-help">
                      Automatically start a new 7-Split cycle after full exit.
                    </p>
                  </div>

                  <div className="modal-footer-actions">
                    <button
                      type="button"
                      onClick={handleUpdateSettingsCancel}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={isUpdatingSettings}
                    >
                      {isUpdatingSettings ? (
                        <span className="btn-loading">
                          <span className="spinner-ring mini"></span> Requesting
                          OTP...
                        </span>
                      ) : (
                        'Request OTP'
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <form
                  onSubmit={handleUpdateSettingsOtpSubmit}
                  className="modern-form"
                >
                  <div className="otp-verification-card">
                    <div className="otp-icon-wrap">
                      <ShieldCheck size={32} weight="duotone" />
                    </div>
                    <h3>Authorize Setting Change</h3>
                    <p>
                      Enter the 6-digit OTP code to update strategy parameters.
                    </p>

                    <div className="otp-input-wrap">
                      <input
                        type="text"
                        name="otp"
                        value={updateSettingsOtpCode}
                        onChange={handleUpdateSettingsOtpInputChange}
                        placeholder="000000"
                        maxLength="6"
                        required
                        inputMode="numeric"
                        pattern="\d{6}"
                        className="otp-code-input mono"
                      />
                    </div>

                    <div
                      className={`otp-timer-badge ${
                        updateSettingsOtpExpiresIn <= 30 ? 'warning' : ''
                      }`}
                    >
                      {updateSettingsOtpExpiresIn > 0
                        ? `Expires in ${Math.floor(updateSettingsOtpExpiresIn / 60)}:${(
                            updateSettingsOtpExpiresIn % 60
                          )
                            .toString()
                            .padStart(2, '0')}`
                        : 'OTP Expired'}
                    </div>
                  </div>

                  <div className="modal-footer-actions">
                    <button
                      type="button"
                      onClick={handleUpdateSettingsCancel}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={isUpdatingSettings}
                    >
                      {isUpdatingSettings ? (
                        <span className="btn-loading">
                          <span className="spinner-ring mini"></span>{' '}
                          Updating...
                        </span>
                      ) : (
                        'Verify & Save Settings'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Investment Modal */}
      {showDeleteOtpInput && (
        <div
          className="modal-overlay"
          onClick={handleDeleteCancel}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') handleDeleteCancel();
          }}
          role="button"
          tabIndex="0"
          aria-label="Close modal"
        >
          <div
            className="modal-dialog"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            role="presentation"
          >
            <div className="modal-header">
              <div className="modal-title-with-icon danger">
                <TrashSimple size={20} weight="bold" />
                <h2>Delete Investment: {tickerToDelete}</h2>
              </div>
              <button className="modal-close-btn" onClick={handleDeleteCancel}>
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="modal-body">
              <div className="destructive-warning-box">
                <WarningCircle
                  size={24}
                  weight="fill"
                  className="warning-icon"
                />
                <div>
                  <p className="warning-title">Irreversible Action</p>
                  <p className="warning-desc">
                    Deleting <strong>{tickerToDelete}</strong> will liquidate
                    active positions and permanently stop the automated 7-Split
                    strategy for this symbol.
                  </p>
                </div>
              </div>

              <form onSubmit={handleDeleteOtpSubmit} className="modern-form">
                <div className="otp-verification-card">
                  <div className="otp-input-wrap">
                    <input
                      type="text"
                      name="otp"
                      value={deleteOtpCode}
                      onChange={handleDeleteOtpInputChange}
                      placeholder="000000"
                      maxLength="6"
                      required
                      inputMode="numeric"
                      pattern="\d{6}"
                      className="otp-code-input mono"
                    />
                  </div>

                  <div
                    className={`otp-timer-badge ${
                      deleteOtpExpiresIn <= 30 ? 'warning' : ''
                    }`}
                  >
                    {deleteOtpExpiresIn > 0
                      ? `Expires in ${Math.floor(deleteOtpExpiresIn / 60)}:${(
                          deleteOtpExpiresIn % 60
                        )
                          .toString()
                          .padStart(2, '0')}`
                      : 'OTP Expired'}
                  </div>
                </div>

                <div className="modal-footer-actions">
                  <button
                    type="button"
                    onClick={handleDeleteCancel}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-danger"
                    disabled={isDeletingInvestment}
                  >
                    {isDeletingInvestment ? (
                      <span className="btn-loading">
                        <span className="spinner-ring mini"></span> Deleting...
                      </span>
                    ) : (
                      'Authorize & Terminate Strategy'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
